"use client";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0E0E0E] text-white">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-semibold mb-4">
          <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
            Investment Bastion — Terms of Service
          </span>
        </h1>
        
        <p className="text-sm text-white/60 mb-12">
          Last updated: 2026-04-16
        </p>

        <div className="prose prose-invert max-w-none space-y-8 text-white/80 leading-relaxed">
          <p>
            These Terms of Service ("Terms") govern your access to and use of the Investment Bastion platform, website, analytics tools, data, ratings, portfolios, and all related services ("Investment Bastion", "Service", "we", "our", "us").
          </p>

          <p>
            Investment Bastion is operated by INBASTION LTD, trading as Investment Bastion, a company registered in England and Wales.
          </p>

          <p>
            By using Investment Bastion, creating an account, or starting a trial, you agree to these Terms. If you do not agree, you may not use the Service.
          </p>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">1. Nature and Scope of the Service</h2>
            <p>
              Investment Bastion provides financial data, analytics dashboards, systematic scoring models, growth estimates, portfolio-construction methodologies, and research tools for informational and educational purposes only. A detailed overview of investment risks is available in our <a href="/risk-disclaimer" className="text-white/80 hover:text-white underline">Risk Disclaimer</a>, which forms part of these Terms.
            </p>
            <p>
              Investment Bastion does not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>provide personalised investment advice;</li>
              <li>recommend buying, selling, or holding any securities;</li>
              <li>manage assets or execute trades;</li>
              <li>provide discretionary or non-discretionary portfolio management;</li>
              <li>act as a broker, financial advisor, or investment manager;</li>
              <li>guarantee performance of any strategy, model, or portfolio;</li>
              <li>engage in any regulated activity under the UK Financial Services and Markets Act 2000.</li>
            </ul>
            <p>
              Investment Bastion is not authorised or regulated by the Financial Conduct Authority (FCA).
            </p>
            <p>
              All investment decisions are solely your responsibility.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">2. Eligibility</h2>
            <p>
              You must be at least 18 years old and provide accurate registration and billing information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">3. Subscription, Billing, and Trial</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.1 Subscription plans</h3>
            <p>
              Investment Bastion offers the following subscription plans:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Free</strong> — available to all registered users. Includes Stock Verdict preview (Key Ratings, Price Momentum and Growth) on any stock, Heatmap and Radar for core free lists, and 1 personal watchlist (5 stocks). Does not include the full Stock Verdict depth (Value onward), full Radar lists, systematic portfolios, or expanded Watchlists.</li>
              <li><strong>Premium</strong> ($29/month or $249/year) — full, unrestricted access to all features: full Stock Verdict on 4,000+ global stocks, Heatmap (all lists and metrics), full Radar (all lists and all strategies), systematic portfolios with monthly rebalancing, and expanded Watchlists (up to 10 watchlists, 100 stocks each).</li>
            </ul>
            <p>
              We reserve the right to modify plan features, pricing, or availability at any time. Material changes to pricing will take effect at the next billing cycle.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.2 Subscription model</h3>
            <p>
              Investment Bastion operates as a recurring subscription service. By subscribing, you authorise us (via Stripe) to charge your payment method at the chosen billing interval.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.3 Billing periods</h3>
            <p>
              Billing occurs monthly or annually.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.4 Auto-renewal</h3>
            <p>
              Subscriptions renew automatically unless you cancel before renewal.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.5 Free trial</h3>
            <p>
              Investment Bastion may offer a free trial of the Premium plan. During the trial:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are not charged until the trial ends.</li>
              <li>Trial includes full Premium access to all features.</li>
              <li>After the trial, you will be charged automatically at the Premium rate ($29/month or $249/year, depending on the billing period selected) unless you cancel before the trial ends.</li>
              <li>You may cancel at any time during the trial to avoid being charged.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.6 Switching billing periods</h3>
            <p>
              You may switch between monthly and annual billing at any time. When you switch:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Changes take effect at the start of your next billing cycle unless otherwise specified.</li>
              <li>Prorated adjustments may apply and are calculated automatically by our payment provider (Stripe).</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.7 Cancellation</h3>
            <p>
              Cancel anytime via your account or the Stripe customer portal. Access remains until the end of the paid period. No partial refunds.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">3.8 Refunds</h3>
            <p>
              All payments for subscriptions to Investment Bastion are non-refundable. This applies to all billing cycles, including monthly and annual plans. Once a paid billing period begins, no refunds will be issued for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>partial billing periods,</li>
              <li>unused time,</li>
              <li>early cancellation,</li>
              <li>forgetting to cancel before renewal,</li>
              <li>dissatisfaction with data, strategies, or methodologies,</li>
              <li>account inactivity,</li>
              <li>account termination due to violation of these Terms.</li>
            </ul>
            <p>
              Refunds may be considered only in exceptional circumstances, such as duplicate charges or billing errors directly caused by us. To request an exception, contact us at{' '}
              <a href="mailto:support@investmentbastion.com" className="text-white/80 hover:text-white underline">
                support@investmentbastion.com
              </a>{' '}
              within 7 days of the charge. We do not guarantee approval of any refund request.
            </p>
            <p>
              Additional details about refunds, billing, and cancellation are provided in our <a href="/refund" className="text-white/80 hover:text-white underline">Refund Policy</a>, which forms part of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">4. Changes to the Service</h2>
            <p>
              Investment Bastion may modify, update, or discontinue any part of the Service at any time. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>metrics, data fields, or computed values;</li>
              <li>growth estimates, forecasts, and scoring models;</li>
              <li>the methodology behind ratings;</li>
              <li>stock universes, screens, or strategies;</li>
              <li>portfolios or strategy availability.</li>
            </ul>
            <p>
              Strategies may be changed, replaced, or discontinued. Methodologies may be improved, updated, or replaced.
            </p>
            <p>
              Investment Bastion is not liable for any impact arising from such changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">5. Methodologies and Calculations</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">5.1 Scoring methodology</h3>
            <p>
              Investment Bastion's ratings, factor scores, classifications, and strategy models are developed internally. They may change as methodologies evolve.
            </p>
            <p>
              Updates may affect:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>rankings,</li>
              <li>stock selections,</li>
              <li>portfolio composition,</li>
              <li>historical results.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">5.2 Strategy availability</h3>
            <p>
              Strategies may be modified, merged, replaced, or removed.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">5.3 Backtests and performance</h3>
            <p>
              All historical performance, simulations, or backtests:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>are based on Investment Bastion's proprietary methodology;</li>
              <li>rely on internally processed datasets;</li>
              <li>are for illustration only.</li>
            </ul>
            <p>
              They do not represent actual trading and may differ significantly from real results.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white/90 mt-6">5.4 Live model portfolios</h3>
            <p>
              Investment Bastion maintains live model portfolios that have been calculated in real time since June 2024. These portfolios:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>are algorithmic and systematic, with decisions made automatically based on multifactor strategy ratings;</li>
              <li>are equal-weighted and rebalanced monthly using closing prices on the last trading day of each month;</li>
              <li>are model portfolios, not actual client accounts;</li>
              <li>do not include brokerage commissions, bid-ask spreads, taxes, or other transaction costs in their performance calculations.</li>
            </ul>
            <p>
              Actual results of following these strategies may differ from model results in either direction.
            </p>
            <p>
              Past performance, whether live or backtested, is not indicative of future results.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">6. Data Sources and Accuracy</h2>
            <p>
              Investment Bastion uses licensed company fundamentals, market data, and analyst consensus estimates from third-party data providers. We calculate proprietary metrics, scores, and rankings on top of that data.
            </p>
            <p>
              Data may contain errors or delays. Metrics, scores, and rankings are generally recalculated daily, although the timing of underlying source data varies by provider and metric.
            </p>
            <p>
              We do not guarantee completeness, accuracy, or timeliness. You are responsible for decisions made using the data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">7. License and Acceptable Use</h2>
            <p>
              You receive a limited, personal, non-transferable license to use Investment Bastion.
            </p>
            <p>
              You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>copy, scrape, or mass-download data;</li>
              <li>redistribute or resell Investment Bastion data, models, or portfolios;</li>
              <li>share your account;</li>
              <li>reverse engineer the platform or scoring models;</li>
              <li>use the Service to build competing products.</li>
            </ul>
            <p>
              Violation may result in account termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">8. No Investment Advice</h2>
            <p>
              Investment Bastion does not provide personalised investment recommendations.
            </p>
            <p>
              You are solely responsible for evaluating financial risks and making decisions.
            </p>
            <p>
              Investment Bastion accepts no responsibility for any losses you incur.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Investment Bastion and INBASTION LTD shall not be liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>investment or trading losses;</li>
              <li>lost profits or opportunity cost;</li>
              <li>inaccuracies in data;</li>
              <li>decisions made based on the Service;</li>
              <li>indirect, incidental, special, or consequential damages.</li>
            </ul>
            <p>
              Total liability for any claim is limited to the subscription fees paid in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">10. Account Security</h2>
            <p>
              You are responsible for your login credentials. We are not liable for losses caused by unauthorised access due to your negligence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">11. Privacy</h2>
            <p>
              Personal data is handled according to our <a href="/privacy" className="text-white/80 hover:text-white underline">Privacy Policy</a>, which is part of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">12. Updates to Terms</h2>
            <p>
              We may update these Terms.
            </p>
            <p>
              Material updates will be communicated.
            </p>
            <p>
              Continued use of the Service equals acceptance of revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">13. Governing Law</h2>
            <p>
              These Terms are governed by the laws of England and Wales.
            </p>
            <p>
              Disputes will be resolved in the courts of England and Wales unless consumer law requires otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white/90 mt-12">14. Contact</h2>
            <p>
              For support or legal inquiries, contact us at:
            </p>
            <p>
              <a href="mailto:support@investmentbastion.com" className="text-white/80 hover:text-white underline">
                support@investmentbastion.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
